import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { ApplicationConfiguration, ApplicationService, ApplicationSettings, IConfigFilter } from '@services/application.service';
import { siteSubscriptions } from '@services/siteSubscriptions';

import { distinctUntilChanged, firstValueFrom, Observable, of } from 'rxjs';
import { CanDeactivateComponent } from 'src/app/guards/PendingChangesGuard.guard';

interface ConfigurationSection {
	id: string;
	name: string;
}

/** AppConfigComponent */
@Component({
	selector: 'app-app-config',
	templateUrl: './app-config.component.html',
	styleUrl: './app-config.component.scss'
})
export class AppConfigComponent implements OnInit, CanDeactivateComponent {

	async confirmNavigation(): Promise<boolean> {

		const OKResult = await firstValueFrom(
			this.globalFunctions.UserConfirm$(
				'You have unsaved changes. Do you want to save them before leaving?',
				`${this.qApplication} Configuration`
			)
		);
		if (OKResult) {
			return await this.save();
		}
		else {
			return true;
		}

	}

	configurationSections: ConfigurationSection[] = [
		{ id: 'tos', name: 'Terms of Service' },
		{ id: 'maintenance', name: 'Maintenance' },
		{ id: 'deployment', name: 'Deployment' }
	];
	configurationTreeFields = {
		dataSource: this.configurationSections,
		id: 'id',
		text: 'name'
	};

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Application Settings */
	public appSettings: ApplicationSettings;
	public originalappSettings: ApplicationSettings;

	/** Application Configuration */
	public appConfig: ApplicationConfiguration;

	/** Environment Variables */
	public env: any

	/** saving */
	saving: boolean = false;

	/** startDateInvalid */
	public startDateInvalid: boolean = false;
	/** endDateInvalid */
	public endDateInvalid: boolean = false;

	public routeValid: boolean = false;

	public qApplication: string = "";
	public qEnvironment: string = "";
	public qDevelopment: boolean = false;
	public activeSection: string;

	/**
	 * constructor
	 * @param applicationService 
	 * @param siteSubscriptions 
	 * @param globalFunctions 
	 */
	constructor(
		private applicationService: ApplicationService,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		private activatedRoute: ActivatedRoute,
	) {

		this.env = environment;
	}

	/** ngOnInit */
	ngOnInit() {

		// Check the Query Parameters
		this.activatedRoute.queryParams.pipe(
			takeUntilDestroyed(this.destroyRef),
			distinctUntilChanged()
		).subscribe(
			async params => {

				if (this.appSettings && this.hasPendingChanges()) {
					const OKResult = await this.confirmNavigation();
					if (!OKResult) {
						return;
					}
				}

				this.routeValid = false;
				try {
					this.qApplication = params['application'] ?? this.env.appName;
					this.qEnvironment = params['environment'] ?? this.env.production ? "Production" : "Development";
					this.qDevelopment = params['development'] ?? (this.env.devsite || this.env.localsite);

					await this.getSettings();

				}
				catch (error) {
					console.log(error);
				}
			}
		);
	}

	async getSettings() {

		try {
			if (this.qApplication && this.qEnvironment) {
				const Result = await this.getApplicationSettings(this.qApplication, this.qEnvironment, this.qDevelopment);
				this.appSettings = Result;
				if (this.appSettings.Application.toLocaleLowerCase() == this.qApplication.toLocaleLowerCase() &&
					this.appSettings.Environment.toLocaleLowerCase() == this.qEnvironment.toLocaleLowerCase()) {

					this.routeValid = true;
					this.appConfig = this.appSettings.Config;

				}
			}
		}
		catch (error) {
			this.routeValid = false;
			this.appConfig = new ApplicationConfiguration();
		}
		finally {
			this.originalappSettings = structuredClone(this.appSettings);
			this.validateMaintenanceDates();
		}
	}

	hasPendingChanges(): boolean {
		return JSON.stringify(this.appSettings) !== JSON.stringify(this.originalappSettings);
	}

	hasToSChanges(): boolean {
		return JSON.stringify(this.originalappSettings.Config.ToS) !== JSON.stringify(this.appSettings.Config.ToS);
	}

	hasMaintenanceChanges(): boolean {
		return JSON.stringify(this.originalappSettings.Config.maintenance) !== JSON.stringify(this.appSettings.Config.maintenance);
	}


	hasDeploymentChanges(): boolean {
		return JSON.stringify(this.originalappSettings.Config.deployment) !== JSON.stringify(this.appSettings.Config.deployment);
	}

	/**
	 * Return the default application configuration.
	 */
	private async getApplicationSettings(application: string, environment: string, development: boolean = false): Promise<ApplicationSettings> {
		try {
			const cfgFilter = <IConfigFilter>{
				application: application,
				environment: environment,
				development: development
			}
			const result = await firstValueFrom(
				this.applicationService.getSettings$(cfgFilter)
			);
			var appSettings = new ApplicationSettings(JSON.parse(result[0].Data));
			return appSettings;
		}
		catch (error: any) {
			if (error.status == 404) {
				console.log(error);
			} else {
				console.log(error);
				this.globalFunctions.HandleError(error, "Error: this.applicationService.getSettings$", false);
			}
		}
		finally { }

		return new ApplicationSettings();
	}


	/**
	 * Convert an epoch value into the format required
	 * by the datetime-local input.
	 * @param epoch 
	 * @returns 
	 */
	getDateTimeLocal(epoch: number | null = Date.now()): string {

		if (epoch === null) {
			return '';
		}

		const date = new Date(epoch);

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');

		return `${year}-${month}-${day}T${hours}:${minutes}`;

	}

	/**
	 * Set the maintenance start time from the Syncfusion datetime picker.
	 * @param input Syncfusion DateTimePicker change event.
	 */
	setStartDateSF(input: any): void {

		if (!input.value) {
			this.appConfig.maintenance.start = null;
			this.validateMaintenanceDates();
			return;
		}

		this.appConfig.maintenance.start = new Date(input.value).getTime();

		this.validateMaintenanceDates();

	}

	/**
	 * Set the maintenance end time from the Syncfusion datetime picker.
	 * @param input Syncfusion DateTimePicker change event.
	 */
	setEndDateSF(input: any): void {

		if (!input.value) {
			this.appConfig.maintenance.end = null;
			this.validateMaintenanceDates();
			return;
		}

		this.appConfig.maintenance.end = new Date(input.value).getTime();

		this.validateMaintenanceDates();

	}

	/**
	 * Validate the maintenance start and end dates.
	 */
	validateMaintenanceDates(): void {

		const start = this.appConfig.maintenance.start;
		const end = this.appConfig.maintenance.end;
		const now = Date.now();

		// Start must not be in the past.
		this.startDateInvalid =
			start != null &&
			start < now;

		// End must be after Start.
		this.endDateInvalid =
			start != null &&
			end != null &&
			end <= start;
	}

	/**
	 * Save the application configuration.
	 */
	async save(): Promise<boolean> {

		try {

			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$(
					`Save Changes to the ${this.qApplication} Application Configuration?`,
					`${this.qApplication} Configuration`
				)
			);

			if (OKResult) {

				/** Update the Config settings with new values */
				this.appSettings.Config = this.appConfig;

				/** Check if er have mainetnance enables and due now */
				if (this.appSettings.Config.maintenance.enabled &&
					this.appSettings.Config.maintenance.start != null &&
					this.appSettings.Config.maintenance.end != null
				) {
					if (this.startDateInvalid || this.endDateInvalid) {
						const start = new Date(this.appSettings.Config.maintenance.start).toLocaleString('en-GB', {
							dateStyle: 'short',
							timeStyle: 'short'
						});

						const end = new Date(this.appSettings.Config.maintenance.end).toLocaleString('en-GB', {
							dateStyle: 'short',
							timeStyle: 'short'
						});

						const maintenanceOK = await firstValueFrom(
							this.globalFunctions.UserConfirm$(
								`Are you sure?\nCheck Maintenance dates\n\tStart:${start}\n\tEnd:${end}`,
								"Check Maintenance Dates"
							)
						);

						if (!maintenanceOK) {
							return false;
						}
					}
				}

				this.saving = true;
				const cfgFilter = <IConfigFilter>{
					application: this.qApplication,
					environment: this.qEnvironment,
					development: this.qDevelopment
				}
				const result = await firstValueFrom(
					this.applicationService.patchSettings$(this.appSettings.ApplicationConfigurationUID, this.appSettings)
				);

				// this.appSettings = <ApplicationSettings>JSON.parse(result[0].Data);
				// this.siteSubscriptions.setApplicationSettings(this.appSettings, true);

				this.globalFunctions.DisplaySnackBar({
					message: "Settings Saved",
					action: ""
				})

				// this.originalappSettings = structuredClone(this.appSettings);
				await this.getSettings()

				return true;
			}
		}
		catch (error) {
			this.globalFunctions.DisplaySnackBar({
				message: "Error Saving Settings",
				action: ""
			})
			return false;
		}
		finally {
			this.saving = false;
		}

		return false;

	}

	/**
	 * Cancel changes and restore the original configuration.
	 */
	cancel() {
		this.getSettings()
	}

	/**
	 * onConfigurationSectionSelected
	 * @param event 
	 */
	onConfigurationSectionSelected(event: any): void {

		const sectionId = event.nodeData.id;

		this.activeSection = sectionId;

		this.scrollToSection(sectionId);
	}


	/**
	 * scrollToSection
	 * @param sectionId 
	 */
	scrollToSection(sectionId: string): void {
		const element = document.getElementById(sectionId);

		if (element) {
			element.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			});
		}

		this.activeSection = sectionId;
	}

}
